// packages/ui/src/contexts/PublicLightLock.tsx

import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { isForcedLightPath, setForcedLightLock } from '@alamo/types';

import { useTheme } from './ThemeContext';

/**
 * Engages the theme core's forced-light lock while the user is on a public
 * MagicLink route (`/p/...`), and releases it on leave — restoring the user's
 * real preference. Mount it INSIDE the router (needs `useLocation`) at the public
 * layout so it only tracks those routes.
 *
 * Why this and not a `dark:` override or a ThemeProvider rewrite: the lock lives
 * in the framework-free theme core and gates `applyResolvedThemeClass`, so ANY
 * actor that resolves to dark (ThemeProvider `setMode`, AuthProvider server theme
 * on login, another tab via the storage event) still leaves the page light while
 * locked — without touching the persisted `mode`. The provider keeps owning the
 * mechanism; this component only flips a boolean the core already respects.
 */
export function PublicLightLock() {
  const { pathname } = useLocation();
  const { mode } = useTheme();
  const forced = isForcedLightPath(pathname);

  useEffect(() => {
    setForcedLightLock(forced, mode);
    // On unmount (leaving the public layout entirely), always release the lock
    // so the app-wide theme is never left pinned to light.
    return () => setForcedLightLock(false, mode);
  }, [forced, mode]);

  return null;
}
